import { useCallback, useEffect, useRef } from 'react';

import '../tracking/events.quiz-bank';

import { type EventMap } from '@zep/logger';
import { trackEvent } from '@zep/logger/tracking';

import { useIsLoggerUserInfoSet } from '@/lib/auth/UserContext';

/**
 * 유저 컨텍스트가 실린 뒤에만 발화하는 `trackEvent`.
 *
 * 게이트가 아니라 큐인 이유: 발화는 미룰 수 있지만 제품 동작(0건 완화 등)은 트래킹 준비를
 * 기다릴 수 없고, 스토리북엔 `UserProvider` 가 없어 영영 준비되지 않는다.
 *
 * @return 렌더마다 같은 함수 — effect 의존성으로 그대로 쓴다.
 */
export const useQuizBankTrack = () => {
  const isReady = useIsLoggerUserInfoSet();
  const isReadyRef = useRef(isReady);
  isReadyRef.current = isReady;
  const queued = useRef<Array<() => void>>([]);

  useEffect(() => {
    if (!isReady) return;
    const pending = queued.current;
    queued.current = [];
    for (const fire of pending) fire();
  }, [isReady]);

  return useCallback(
    <K extends keyof EventMap & string>(name: K, props: EventMap[K]) => {
      const fire = () => trackEvent(name, props);
      if (isReadyRef.current) {
        fire();
        return;
      }
      queued.current.push(fire);
    },
    [],
  );
};
